import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Transition } from '../../components/Transition';
import { useAppDispatch, useAppSelector } from '../../store';
import { useFormik } from 'formik';
import { Users } from '../../store/users/api/interfaces';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { CompanyLicenses, CompanyLicenseRenewals, BrokerLicenses, BrokerLicenseRenewals } from '../../client/services';
import { findUser } from '../../store/users/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Divider } from '@mui/material';
import { getAttachmentAssetUrl, getAttachmentPreviewSrc } from '../../utils/url';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  actionUser: Users | undefined;
  search?: string | undefined;
  sortBy?: string | undefined;
  sortByOrder?: string | undefined;
  limit?: number;
  skip?: number;
  onApprovalSuccess?: () => void; // Callback when renewal is approved
  onRejectionSuccess?: () => void; // Callback when renewal is rejected
}

interface IInitialState {
  expirationDate: Date | null;
  licenseId?: string;
  renewalRequestId?: string;
  rejectionReason?: string;
  actionType?: 'approve' | 'reject' | 'update';
}

const licenseManagementSchema = Yup.object().shape({
  expirationDate: Yup.date()
    .when('actionType', {
      is: (val: string) => val === 'approve' || val === 'update',
      then: (schema) => schema
        .required('Expiration Date is required')
        .min(new Date(), 'Expiration date must be in the future'),
      otherwise: (schema) => schema.nullable(),
    })
    .label('Expiration Date'),
  rejectionReason: Yup.string()
    .when('actionType', {
      is: 'reject',
      then: (schema) => schema
        .required('Rejection reason is required')
        .min(10, 'Rejection reason must be at least 10 characters')
        .max(500, 'Rejection reason must not exceed 500 characters'),
      otherwise: (schema) => schema.nullable(),
    })
    .label('Rejection Reason'),
});

const LicenseManagementModal: React.FC<IProps> = ({
  toggle,
  onClose,
  actionUser,
  search,
  sortBy,
  sortByOrder,
  limit,
  skip,
  onApprovalSuccess,
  onRejectionSuccess,
}) => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [currentLicense, setCurrentLicense] = useState<any>(null);
  const [pendingRenewal, setPendingRenewal] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    values,
  } = useFormik<IInitialState>({
    initialValues: {
      expirationDate: null,
      licenseId: undefined,
      renewalRequestId: undefined,
      rejectionReason: undefined,
      actionType: undefined,
    },
    validationSchema: licenseManagementSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!actionUser?.id) {
        toast.error('User information is missing');
        return;
      }

      // Validate based on action type
      if (values.actionType === 'approve' && !values.expirationDate) {
        toast.error('Please select an expiration date');
        return;
      }
      if (values.actionType === 'reject' && !values.rejectionReason) {
        toast.error('Please provide a rejection reason');
        return;
      }
      if (values.actionType === 'update' && !values.expirationDate) {
        toast.error('Please select an expiration date');
        return;
      }

      try {
        setLoading(true);

        // Determine if this is a broker or company
        const isBroker = actionUser.userRole === 'broker';
        const LicenseService = isBroker ? BrokerLicenses : CompanyLicenses;
        const RenewalService = isBroker ? BrokerLicenseRenewals : CompanyLicenseRenewals;
        const idField = isBroker ? 'brokerId' : 'companyId';
        const licenseDocumentType = isBroker ? 'broker_license' : 'trade_license';
        const userType = isBroker ? 'broker' : 'company';

        // Handle rejection
        if (values.actionType === 'reject' && values.renewalRequestId) {
          await RenewalService.patch(values.renewalRequestId, {
            status: 'rejected',
            rejectionReason: values.rejectionReason,
          });
          toast.success(`License renewal request rejected successfully. The ${userType} has been notified.`);
          
          // Notify parent component of successful rejection
          if (onRejectionSuccess) {
            onRejectionSuccess();
          }
        }
        // Handle approval
        else if (values.actionType === 'approve' && values.renewalRequestId && values.expirationDate) {
          // Format date as YYYY-MM-DD for backend
          const expirationDateStr = moment(values.expirationDate).format('YYYY-MM-DD');
          
          await RenewalService.patch(values.renewalRequestId, {
            status: 'approved',
            requestedExpirationDate: expirationDateStr,
          });
          toast.success(`License renewal request approved successfully. The ${userType} account has been reactivated.`);
          
          // Notify parent component of successful approval
          if (onApprovalSuccess) {
            onApprovalSuccess();
          }
        }
        // Handle regular license update
        else if (values.actionType === 'update' && values.licenseId && values.expirationDate) {
          // Format date as YYYY-MM-DD for backend
          const expirationDateStr = moment(values.expirationDate).format('YYYY-MM-DD');
          
          await LicenseService.patch(values.licenseId, {
            expirationDate: expirationDateStr,
          });
          toast.success('License expiration date updated successfully');
        }
        // Create new license (shouldn't happen for approved users, but handle it)
        else if (!values.licenseId && values.expirationDate) {
          const expirationDateStr = moment(values.expirationDate).format('YYYY-MM-DD');
          const licenseAsset = actionUser.attachments?.find(
            (att: any) => att.documentType === licenseDocumentType
          );

          await LicenseService.create({
            [idField]: actionUser.id,
            licenseAssetId: licenseAsset?.id || null,
            expirationDate: expirationDateStr,
            isActive: true,
          });
          toast.success('License created successfully');
        } else {
          toast.error('Invalid action. Please select an expiration date or provide a rejection reason.');
        }

        // Refresh users list if limit/skip/search/sortBy are provided
        if (limit !== undefined || skip !== undefined) {
          dispatch(
            findUser({
              limit: limit || 25,
              skip: skip || 0,
              ...(search && { search: search }),
              ...(sortBy && { sortByName: sortBy }),
              ...(sortByOrder && { sortByOrder: sortByOrder }),
            })
          );
        }

        // Reset form and close modal
        resetForm();
        setPendingRenewal(null);
        setCurrentLicense(null);
        setFieldValue('actionType', undefined);
        onClose();
      } catch (error: any) {
        console.error('Error updating license:', error);
        const errorMessage = error?.message || error?.response?.data?.message || 'Failed to update license';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch current license and pending renewal when modal opens
  useEffect(() => {
    if (toggle && actionUser?.id) {
      fetchLicenseData();
    } else {
      // Reset state when modal closes
      setCurrentLicense(null);
      setPendingRenewal(null);
      resetForm();
      setFieldValue('actionType', undefined);
    }
  }, [toggle, actionUser?.id]);

  const fetchLicenseData = async () => {
    if (!actionUser?.id) return;
    
    try {
      setLoading(true);
      
      // Fetch current license - use appropriate service based on user role
      const isBroker = actionUser.userRole === 'broker';
      const LicenseService = isBroker ? BrokerLicenses : CompanyLicenses;
      const idField = isBroker ? 'brokerId' : 'companyId';
      
      const licenses = await LicenseService.find({
        query: {
          [idField]: actionUser.id,
          isActive: true,
          $limit: 1,
        },
      });
      
      if (licenses?.data && licenses.data.length > 0) {
        const license = licenses.data[0];
        setCurrentLicense(license);
        const expDate = license.expirationDate 
          ? new Date(license.expirationDate) 
          : null;
        setFieldValue('expirationDate', expDate);
        setFieldValue('licenseId', license.id);
      } else {
        setCurrentLicense(null);
        setFieldValue('expirationDate', null);
        setFieldValue('licenseId', undefined);
      }

      // Fetch pending renewal request - use appropriate service based on user role
      const RenewalService = isBroker ? BrokerLicenseRenewals : CompanyLicenseRenewals;
      const renewals = await RenewalService.find({
        query: {
          [idField]: actionUser.id,
          status: 'pending',
          $limit: 1,
          $sort: { createdAt: -1 },
        },
      });

      if (renewals?.data && renewals.data.length > 0) {
        setPendingRenewal(renewals.data[0]);
        setFieldValue('renewalRequestId', renewals.data[0].id);
      } else {
        setPendingRenewal(null);
        setFieldValue('renewalRequestId', undefined);
      }
    } catch (error) {
      console.error('Error fetching license data:', error);
      setCurrentLicense(null);
      setPendingRenewal(null);
    } finally {
      setLoading(false);
    }
  };

  const getLicenseStatus = () => {
    if (!currentLicense) return null;

    const expirationDate = moment(currentLicense.expirationDate);
    const today = moment();
    const gracePeriodStart = moment(currentLicense.gracePeriodStartDate);

    if (today.isAfter(gracePeriodStart)) {
      return {
        type: 'error' as const,
        message: 'License expired and grace period ended. Account should be suspended.',
      };
    } else if (today.isAfter(expirationDate)) {
      const daysInGrace = today.diff(expirationDate, 'days');
      return {
        type: 'warning' as const,
        message: `License expired. Grace period active (${90 - daysInGrace} days remaining).`,
      };
    } else {
      const daysUntilExpiry = expirationDate.diff(today, 'days');
      if (daysUntilExpiry <= 30) {
        return {
          type: 'warning' as const,
          message: `License expires in ${daysUntilExpiry} days.`,
        };
      }
      return {
        type: 'success' as const,
        message: `License valid until ${expirationDate.format('YYYY-MM-DD')}.`,
      };
    }
  };

  const status = getLicenseStatus();

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        setPendingRenewal(null);
        setCurrentLicense(null);
        setFieldValue('actionType', undefined);
        onClose();
      }}
      aria-describedby='license-management-dialog'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
        Manage {actionUser?.userRole === 'broker' ? 'Broker' : 'Company'} License - {actionUser?.name}
      </DialogTitle>

      <form onSubmit={handleSubmit} autoComplete='off'>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography>Loading license information...</Typography>
            </Box>
          ) : (
            <>
              {status && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                  {status.message}
                </Alert>
              )}

              {pendingRenewal && (
                <Alert severity='info' sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Pending License Renewal Request
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 1 }}>
                    Request Date: {moment(pendingRenewal.createdAt).format('YYYY-MM-DD')}
                  </Typography>
                  {pendingRenewal.newLicenseAsset && (() => {
                    const asset = pendingRenewal.newLicenseAsset;
                    const assetUrl = getAttachmentAssetUrl(asset, accessToken);
                    const previewSrc = getAttachmentPreviewSrc(asset, accessToken);
                    const isImage = asset.fileType?.split('/')[0] === 'image';
                    const isPdf = asset.fileType === 'application/pdf';
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                          New License Document:
                        </Typography>
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 2,
                            },
                          }}
                          onClick={() => assetUrl && window.open(assetUrl, '_blank')}
                        >
                          {isImage ? (
                            <img
                              src={previewSrc}
                              alt='License Preview'
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px 4px 0 0',
                              }}
                            />
                          ) : isPdf ? (
                            <img
                              src='/static/pdf.png'
                              alt='PDF'
                              style={{ width: '60px', height: '60px' }}
                            />
                          ) : (
                            <img
                              src='/static/docket.png'
                              alt='Document'
                              style={{ width: '60px', height: '60px' }}
                            />
                          )}
                          <Typography
                            variant='caption'
                            sx={{
                              mt: 0.5,
                              px: 1,
                              textAlign: 'center',
                              fontSize: '0.7rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            License Preview
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Alert>
              )}

              {currentLicense && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Current Expiration: {moment(currentLicense.expirationDate).format('YYYY-MM-DD')}
                  </Typography>
                  {currentLicense.gracePeriodStartDate && (
                    <>
                      <Typography variant='body2' color='text.secondary'>
                        Grace Period Starts: {moment(currentLicense.gracePeriodStartDate).format('YYYY-MM-DD')}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Grace Period Ends: {moment(currentLicense.gracePeriodStartDate).add(90, 'days').format('YYYY-MM-DD')}
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {pendingRenewal && <Divider sx={{ my: 2 }} />}

              <Grid container spacing={2}>
                {/* Expiration Date - Show for approval or regular update */}
                {(!pendingRenewal || values.actionType !== 'reject') && (
                  <Grid item xs={12} sm={12}>
                    <FormControl
                      component='fieldset'
                      error={Boolean(errors.expirationDate && touched.expirationDate)}
                      fullWidth
                    >
                      <FormLabel>
                        {pendingRenewal ? 'Set Expiration Date (Approve Renewal)' : 'License Expiration Date'}
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label={pendingRenewal ? 'New Expiration Date' : 'New Expiration Date'}
                          value={values.expirationDate}
                          onChange={(newValue) => {
                            setFieldValue('expirationDate', newValue);
                            if (pendingRenewal && newValue) {
                              setFieldValue('actionType', 'approve');
                              setFieldValue('rejectionReason', undefined);
                            } else if (!pendingRenewal && newValue) {
                              setFieldValue('actionType', 'update');
                            }
                          }}
                          minDate={new Date()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={Boolean(
                                errors.expirationDate && touched.expirationDate
                              )}
                              helperText={
                                Boolean(
                                  errors.expirationDate && touched.expirationDate
                                ) && errors?.expirationDate
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {Boolean(errors.expirationDate && touched.expirationDate) && (
                        <FormHelperText>{errors.expirationDate}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {/* Rejection Reason - Show when there's a pending renewal */}
                {pendingRenewal && (
                  <Grid item xs={12} sm={12}>
                    <FormControl
                      component='fieldset'
                      error={Boolean(errors.rejectionReason && touched.rejectionReason)}
                      fullWidth
                    >
                      <FormLabel>Rejection Reason (Required for Rejection)</FormLabel>
                      <TextField
                        name='rejectionReason'
                        multiline
                        rows={4}
                        fullWidth
                        placeholder='Please provide a detailed reason for rejecting this renewal request...'
                        value={values.rejectionReason || ''}
                        onChange={(e) => {
                          setFieldValue('rejectionReason', e.target.value);
                          if (e.target.value && e.target.value.trim().length >= 10) {
                            setFieldValue('actionType', 'reject');
                            setFieldValue('expirationDate', null);
                          } else if (!e.target.value || e.target.value.trim().length < 10) {
                            setFieldValue('actionType', undefined);
                          }
                        }}
                        onBlur={() => {
                          setFieldValue('rejectionReason', values.rejectionReason);
                        }}
                        error={Boolean(errors.rejectionReason && touched.rejectionReason)}
                        helperText={
                          Boolean(errors.rejectionReason && touched.rejectionReason)
                            ? errors.rejectionReason
                            : 'Minimum 10 characters required. Fill this to reject the renewal.'
                        }
                      />
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              setPendingRenewal(null);
              setCurrentLicense(null);
              setFieldValue('actionType', undefined);
              onClose();
            }}
            variant='outlined'
            disabled={loading}
          >
            {'Cancel'}
          </Button>
          
          {/* Reject Button - Show only when there's a pending renewal */}
          {pendingRenewal && (
            <LoadingButton
              onClick={() => {
                setFieldValue('actionType', 'reject');
                // Validate rejection reason before submitting
                if (!values.rejectionReason || values.rejectionReason.trim().length < 10) {
                  toast.error('Please provide a rejection reason (minimum 10 characters)');
                  return;
                }
                // Submit rejection - validation will be handled in onSubmit
                handleSubmit();
              }}
              loading={isSubmitting || loading}
              variant='outlined'
              color='error'
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {'Reject Renewal'}
            </LoadingButton>
          )}
          
          {/* Approve/Update Button */}
          <LoadingButton
            type='submit'
            onClick={() => {
              if (pendingRenewal) {
                setFieldValue('actionType', 'approve');
              } else if (values.licenseId) {
                setFieldValue('actionType', 'update');
              }
            }}
            loading={isSubmitting || loading}
            variant='contained'
            disabled={loading}
          >
            {pendingRenewal ? 'Approve Renewal' : 'Update License'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LicenseManagementModal;

